import { useState, useMemo, forwardRef, useEffect } from "react";

import Box from "@/components/Box";
import CustomerPage from "@/components/CustomerPage";
import CustomerTabs from "@/components/CustomerTabs";
import DataLoader from "@/components/DataLoader";
import Grid from "@/components/Grid";
import PartnerDashboardBox from "@/components/boxes/PartnerDashboardBox";
import useDocTitle from "@/hooks/use-doc-title";
import PenIcon from "@/images/PenIcon";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Center from "@/tpo/Center";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import Pagination from "@/tpo/Pagination";
import Radio from "@/tpo/Radio";
import Stack from "@/tpo/Stack";
import B2CHeader from "@/tpo/header/B2CHeader";
import ButtonV2 from "@/v2/Buttons";
import { useMutation } from "@apollo/client";
import { useTheme } from "styled-components";

const Container = forwardRef(({ children, ...props }, ref) => {
  const theme = useTheme();
  return (
    <Box
      ref={ref}
      {...props}
      bg="haze"
      px={20}
      pt={theme.spacing.section.pt}
      pb={theme.spacing.section.pb}
    >
      <Box maxWidth={1532} mx="auto">
        {children}
      </Box>
    </Box>
  );
});

function getAnswerOptions(answerConfig) {
  if (!answerConfig) return [];

  const answers = [];
  if (answerConfig.answer0) answers.push({ value: 0, label: answerConfig.answer0 });
  if (answerConfig.answer1) answers.push({ value: 1, label: answerConfig.answer1 });
  if (answerConfig.answer2) answers.push({ value: 2, label: answerConfig.answer2 });
  if (answerConfig.answer3) answers.push({ value: 3, label: answerConfig.answer3 });

  return answers;
}

function GetStartedView({ onStart, questionCount }) {
  return (
    <Stack gap={[20, 20, 40]} alignItems="center">
      <Stack gap={20}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderWidth={4}
          width={115}
          height={115}
          borderRadius={40}
          borderColor="black"
          borderStyle="solid"
          mx="auto"
        >
          <PenIcon size={40} variant="fill" />
        </Box>
        <Box
          fontFamily="gilroyBold"
          fontSize={[18, 18, 24]}
          lineHeight="100%"
          letterSpacing="2px"
          textTransform="uppercase"
          textAlign="center"
        >
          Self assessment questionnaire
        </Box>
      </Stack>
      <Box fontFamily="gilroyMedium" textAlign="left" fontSize={[14, 14, 16]} lineHeight={["28px"]}>
        Complete this short {questionCount} question survey to help build a clearer picture of how
        you may be affected day to day. Your DNA shows your underlying tendencies, while your
        symptoms reveal how these processes are working in real life.
        <br />
        <br />
        Please answer based on your recent experience. Your responses will be combined with your DNA
        and test results to give your practitioner a more personalised understanding of your health.
      </Box>
      <ButtonV2
        color="dark"
        rightIcon={<ChevronComponent />}
        onClick={onStart}
        size={["sm", "sm", "md"]}
      >
        get started
      </ButtonV2>
    </Stack>
  );
}

function QuestionView({
  question,
  questionNumber,
  totalQuestions,
  answeredCount,
  selectedAnswer,
  onAnswerChange,
  onPrevious,
  onNext,
  onPageChange,
  getPageButtonProps,
  showFinishLaterButton,
  onFinishLater
}) {
  const answerOptions = useMemo(
    () => getAnswerOptions(question.answerConfig),
    [question.answerConfig]
  );

  const pageInfo = {
    totalPages: totalQuestions,
    currentPage: questionNumber,
    hasNextPage: questionNumber < totalQuestions,
    hasPreviousPage: questionNumber > 1
  };

  const isFirstQuestion = questionNumber === 1;

  const showPreviousButton = !isFirstQuestion;
  const showNextButton = selectedAnswer !== null;

  const bottom = (
    <Stack bg="white" gap={20} pt={20} pb={40} px={20}>
      <Box maxWidth={802} mx="auto">
        <Stack gap={10}>
          <Box textTransform="uppercase" fontFamily="gilroyBold" fontSize={10}>
            Questions
          </Box>
          <Pagination
            pageInfo={pageInfo}
            onChange={onPageChange}
            siblingCount={[1, 1, 4]}
            showArrows={[false, false, true]}
            getPageButtonProps={getPageButtonProps}
          />
        </Stack>
        <ProgressBar current={answeredCount} total={totalQuestions} />
      </Box>
    </Stack>
  );

  return (
    <>
      <Container>
        <PartnerDashboardBox maxWidth={1024} pt={60} pb={80} borderRadius={20} px={20}>
          <Stack gap={[20, 20, 40]}>
            <Stack gap={8}>
              <Box fontFamily="gilroyBold" fontSize={[18, 18, 24]} lineHeight="150%">
                Question {questionNumber}
              </Box>
              <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="150%">
                {question.text}
              </Box>
            </Stack>
            <Grid gridTemplateColumns={["1fr", "1fr", "1fr 1fr"]} gridColumnGap={20}>
              {answerOptions.map(option => (
                <Radio
                  key={option.value}
                  name={`question-${question.id}`}
                  value={option.value}
                  label={option.label}
                  checked={selectedAnswer === option.value}
                  onChange={onAnswerChange}
                  labelProps={{
                    fontSize: [14, 14, 16]
                  }}
                />
              ))}
            </Grid>
            <Group justifyContent={showPreviousButton ? "space-between" : "center"}>
              {showPreviousButton && (
                <ButtonV2
                  color="dark"
                  leftIcon={<ChevronComponent style={{ transform: "rotate(180deg)" }} />}
                  onClick={onPrevious}
                  size={["xs", "xs", "md"]}
                >
                  previous
                </ButtonV2>
              )}
              <ButtonV2
                color="dark"
                rightIcon={<ChevronComponent />}
                onClick={onNext}
                disabled={!showNextButton}
                size={["xs", "xs", "md"]}
              >
                next
              </ButtonV2>
            </Group>
            {showFinishLaterButton && (
              <Center>
                <ButtonV2
                  variant="link"
                  color="dark"
                  sx={{
                    textDecoration: "underline !important",
                    fontSize: [14, 14, 16],
                    textTransform: "none",
                    fontFamily: "gilroyMedium",
                    letterSpacing: 0,
                    p: 0,
                    height: "auto"
                  }}
                  onClick={onFinishLater}
                >
                  Finish submission later
                </ButtonV2>
              </Center>
            )}
          </Stack>
        </PartnerDashboardBox>
      </Container>
      {bottom}
    </>
  );
}

function ProgressBar({ current, total }) {
  const percentage = Math.round((current / total) * 100);

  return (
    <Box>
      <Box
        pb="10px"
        fontFamily="gilroyBold"
        fontSize={10}
        pt={40}
        mt={20}
        textTransform="uppercase"
      >
        Complete: {percentage}%
      </Box>
      <Box height={8} bg="haze" borderRadius={6} overflow="hidden">
        <Box
          height="100%"
          bg="purple"
          borderRadius={6}
          width={`${percentage}%`}
          style={{ transition: "width 0.3s ease" }}
        />
      </Box>
    </Box>
  );
}

function SubmitView({ additionalNotes, onNotesChange, onSubmit }) {
  return (
    <PanelBoxV2
      maxWidth={760}
      outer={{
        pt: [30, 30, 60],
        pb: [50, 50, 80],
        px: [20],
        bg: "white"
      }}
    >
      <Stack gap={[20, 20, 40]}>
        <Box fontFamily="gilroyBold" fontSize={[24, 24, 32]} lineHeight="130%">
          Submit
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={[16, 16, 18]} lineHeight="150%">
          Thank you for answering all of the questionnaire. If you would like to note any additional
          information please do so below. Once you are happy with the answers you can submit your
          answers below.
        </Box>
        <Box
          as="textarea"
          width="100%"
          minHeight="150px"
          p={20}
          fontFamily="gilroyMedium"
          fontSize={[14, 14, 16]}
          borderWidth="1px"
          borderStyle="solid"
          borderColor="midGrey"
          borderRadius={5}
          resize="vertical"
          value={additionalNotes}
          onChange={e => onNotesChange(e.target.value)}
          placeholder="Additional notes (optional)"
        />
        <Box pt={20}>
          <ButtonV2
            color="green"
            rightIcon={<ChevronComponent />}
            onClick={onSubmit}
            size={["sm", "sm", "md"]}
          >
            submit
          </ButtonV2>
        </Box>
      </Stack>
    </PanelBoxV2>
  );
}

function SuccessView({ onContinue }) {
  return (
    <Stack gap={[8]}>
      <Box fontFamily="gilroyBold" fontSize={[24]} lineHeight="150%">
        Success
      </Box>
      <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="150%">
        Your answers have been recorded on your profile. Your practitioner will now have access to
        these to help inform any future consultation.
      </Box>
      {onContinue && (
        <Center pt={40}>
          <ButtonV2 color="dark" onClick={onContinue} size={["sm", "sm", "md"]}>
            close and continue
          </ButtonV2>
        </Center>
      )}
    </Stack>
  );
}

function QuestionnaireContent({
  userId,
  questions,
  userSubmission,
  SUBMIT_USER_ANSWER_MUTATION,
  SUBMIT_USER_QUESTIONNAIRE_MUTATION,
  CREATE_USER_SUBMISSION_MUTATION,
  onContinue,
  showFinishLaterButton
}) {
  const hasSubmission = !!userSubmission;

  const initialAnswers = useMemo(() => {
    if (!hasSubmission || !userSubmission.answers) return {};
    return userSubmission.answers.reduce((acc, answer) => {
      acc[answer.question.id] = answer.answer;
      return acc;
    }, {});
  }, [userSubmission, hasSubmission]);

  const initialQuestionIndex = useMemo(() => {
    if (!hasSubmission || !userSubmission.answers?.length) return 0;
    const answerMap = userSubmission.answers.reduce((acc, a) => {
      acc[a.question.id] = true;
      return acc;
    }, {});
    for (let i = questions.length - 1; i >= 0; i--) {
      if (answerMap[questions[i].id]) return i;
    }
    return 0;
  }, [userSubmission, hasSubmission, questions]);

  const [submissionId, setSubmissionId] = useState(hasSubmission ? userSubmission.id : null);
  const [started, setStarted] = useState(hasSubmission);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(initialQuestionIndex);
  const [answers, setAnswers] = useState(initialAnswers);
  const [showSubmit, setShowSubmit] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [submitUserAnswer] = useMutation(SUBMIT_USER_ANSWER_MUTATION);
  const [submitUserQuestionnaire] = useMutation(SUBMIT_USER_QUESTIONNAIRE_MUTATION);
  const [createUserSubmission] = useMutation(CREATE_USER_SUBMISSION_MUTATION);

  const currentQuestion = questions[currentQuestionIndex];
  const selectedAnswer = currentQuestion ? answers[currentQuestion.id] ?? null : null;
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  const answeredCount = questions.filter(q => answers[q.id] !== undefined).length;

  const handleStart = async () => {
    const { data } = await createUserSubmission({
      variables: { input: { userId } }
    });
    const newSubmissionId = data?.createUserSubmissionMutation?.submissionId;
    if (newSubmissionId) {
      setSubmissionId(newSubmissionId);
      setStarted(true);
    }
  };

  const handleAnswerChange = value => {
    if (currentQuestion) {
      setAnswers(prev => ({
        ...prev,
        [currentQuestion.id]: value
      }));

      submitUserAnswer({
        variables: {
          input: {
            submission: submissionId,
            question: currentQuestion.id,
            answer: value,
            userId
          }
        }
      });
    }
  };

  const handleNext = () => {
    if (isLastQuestion) {
      setShowSubmit(true);
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const { data } = await submitUserQuestionnaire({
      variables: {
        input: {
          id: submissionId,
          complete: true,
          additionalInformation: additionalNotes,
          userId
        }
      }
    });

    const errors = data?.submitUserQuestionnaireMutation?.errors;
    if (!errors || errors.length === 0) {
      setSubmitted(true);
    }
  };

  const handlePageChange = pageNumber => {
    setCurrentQuestionIndex(pageNumber - 1);
  };

  const getPageButtonProps = (pageNumber, currentPage) => {
    if (currentPage === pageNumber) {
      return { bg: "dark", color: "white", disabled: false };
    }

    const questionIndex = pageNumber - 1;
    const question = questions[questionIndex];
    if (!question) {
      return { bg: "haze", color: "midGrey", disabled: true };
    }

    if (answers[question.id] !== undefined) {
      return { bg: "green", color: "white", disabled: false };
    }

    return { bg: "haze", color: "midGrey", disabled: true };
  };

  if (!started) {
    return (
      <Container>
        <PartnerDashboardBox maxWidth={1024} pt={60} pb={80} borderRadius={20} px={20}>
          <GetStartedView onStart={handleStart} questionCount={questions.length} />
        </PartnerDashboardBox>
      </Container>
    );
  }

  if (submitted) {
    return (
      <Container>
        <PartnerDashboardBox maxWidth={1024} pt={60} pb={80} borderRadius={20} px={20}>
          <SuccessView onContinue={onContinue} />
        </PartnerDashboardBox>
      </Container>
    );
  }

  if (showSubmit) {
    return (
      <Container>
        <PartnerDashboardBox maxWidth={1024} pt={60} pb={80} borderRadius={20} px={20}>
          <SubmitView
            additionalNotes={additionalNotes}
            onNotesChange={setAdditionalNotes}
            onSubmit={handleSubmit}
          />
        </PartnerDashboardBox>
      </Container>
    );
  }

  if (!currentQuestion) {
    return null;
  }

  return (
    <QuestionView
      question={currentQuestion}
      questionNumber={currentQuestionIndex + 1}
      totalQuestions={questions.length}
      answeredCount={answeredCount}
      selectedAnswer={selectedAnswer}
      onAnswerChange={handleAnswerChange}
      onPrevious={handlePrevious}
      onNext={handleNext}
      onPageChange={handlePageChange}
      getPageButtonProps={getPageButtonProps}
      showFinishLaterButton={showFinishLaterButton}
      onFinishLater={onContinue}
    />
  );
}

export default function Questionnaire({
  QUESTIONNAIRE_QUERY,
  SUBMIT_USER_ANSWER_MUTATION,
  SUBMIT_USER_QUESTIONNAIRE_MUTATION,
  CREATE_USER_SUBMISSION_MUTATION,
  userId,
  onContinue,
  showFinishLaterButton
}) {
  useDocTitle("Questionnaire");

  return (
    <>
      <DataLoader
        query={QUESTIONNAIRE_QUERY}
        variables={{ userId }}
        fetchPolicy="network-only"
        nextFetchPolicy="network-only"
        render={({ questions, userSubmission }) => (
          <QuestionnaireContent
            questions={questions}
            userSubmission={userSubmission}
            SUBMIT_USER_ANSWER_MUTATION={SUBMIT_USER_ANSWER_MUTATION}
            SUBMIT_USER_QUESTIONNAIRE_MUTATION={SUBMIT_USER_QUESTIONNAIRE_MUTATION}
            CREATE_USER_SUBMISSION_MUTATION={CREATE_USER_SUBMISSION_MUTATION}
            userId={userId}
            onContinue={onContinue}
            showFinishLaterButton={showFinishLaterButton}
          />
        )}
      />
    </>
  );
}
